import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Clock, ChevronRight, AlertTriangle, Square, ArrowRight, Pause, Play, Search, ChevronDown, RefreshCw, Terminal, X as XIcon, Share2, Check, Eye, Image as ImageIcon, Loader2 } from 'lucide-react';
import { API } from '../App';
import { ReactionBar } from '../components/ReactionBar';
import { ClipButton } from '../components/ClipButton';
import { PollPanel } from '../components/PollPanel';
import CatFightTheater from '../components/CatFightTheater';
import AssetPanel from '../components/AssetPanel';
import VoiceAvatar from '../components/VoiceAvatar';

const TIER_COLORS = { free: '#A0A0A0', economy: '#22C55E', standard: '#F59E0B', premium: '#EF4444' };

// Vision-capable model prefixes — mirrors backend renderer_service.py
const VISION_PREFIXES = [
  'openai/gpt-4o', 'openai/gpt-4.1', 'anthropic/claude-sonnet-4',
  'anthropic/claude-opus-4', 'anthropic/claude-3', 'google/gemini-2',
  'google/gemini-3', 'x-ai/grok',
];
function isVisionCapable(model) {
  if (!model) return false;
  return VISION_PREFIXES.some(p => model.startsWith(p));
}

const TENSION_COLORS = {
  groupthink_risk: '#22C55E',
  balanced: '#F59E0B',
  high_tension: '#FF6B00',
  chaos_risk: '#EF4444',
};

export default function Theater() {
  const { frayId } = useParams();
  const navigate = useNavigate();
  const [fray, setFray] = useState(null);
  const [messages, setMessages] = useState([]);
  const [activeVoice, setActiveVoice] = useState(null);
  const [currentPhase, setCurrentPhase] = useState(null);
  const [tension, setTension] = useState({ score: 0, classification: 'balanced' });
  const [verdict, setVerdict] = useState(null);
  const [voiceStates, setVoiceStates] = useState({});
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [voiceError, setVoiceError] = useState(null); // {voice, model, message, voice_id}
  const [availableModels, setAvailableModels] = useState([]);
  const [modelSearch, setModelSearch] = useState('');
  const [voiceStats, setVoiceStats] = useState({}); // {voice_id: {tokens, cost, time, status}}
  const [roundUsage, setRoundUsage] = useState([]); // round summaries
  const [frayTotalCost, setFrayTotalCost] = useState(0);
  const [frayTotalTokens, setFrayTotalTokens] = useState(0);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLogs, setDebugLogs] = useState([]); // real-time debug entries
  const [renderEvents, setRenderEvents] = useState([]); // render_start/render_complete SSE events
  const [shareCopied, setShareCopied] = useState(false);
  const debugEndRef = useRef(null);
  const messagesEndRef = useRef(null);
  const streamingRef = useRef(false);
  const abortRef = useRef(null);

  // Load available models for the error dialog
  useEffect(() => {
    API.get('/api/byok/models').then(r => setAvailableModels(r.data.models || [])).catch(() => {});
  }, []);

  const handleStop = async () => {
    try {
      await API.post(`/api/frays/${frayId}/stop`);
      if (abortRef.current) abortRef.current.abort();
      setIsStreaming(false);
      setIsPaused(false);
      streamingRef.current = false;
    } catch (err) {
      console.error('Stop failed:', err);
    }
  };

  const handlePause = async () => {
    try {
      await API.post(`/api/frays/${frayId}/pause`);
      setIsPaused(true);
    } catch (err) {
      console.error('Pause failed:', err);
    }
  };

  const handleResume = async () => {
    try {
      await API.post(`/api/frays/${frayId}/resume`);
      setIsPaused(false);
      setVoiceError(null);
      setModelSearch('');
    } catch (err) {
      console.error('Resume failed:', err);
    }
  };

  const handleChangeModelAndResume = async (newModelId) => {
    if (!voiceError) return;
    try {
      // Update the voice's model
      await API.put(`/api/frays/${frayId}/voices/${encodeURIComponent(voiceError.voice)}/model`, { model: newModelId });
      // Update the local fray data so Voice Rail shows correct model
      setFray(prev => {
        if (!prev) return prev;
        const updatedVoices = prev.voices.map(v =>
          v.name === voiceError.voice ? { ...v, default_model: newModelId } : v
        );
        return { ...prev, voices: updatedVoices };
      });
      // Resume the Fray
      await API.post(`/api/frays/${frayId}/resume`);
      setIsPaused(false);
      setVoiceError(null);
      setModelSearch('');
    } catch (err) {
      console.error('Model change failed:', err);
    }
  };

  const [showStoppedOverlay, setShowStoppedOverlay] = useState(false);

  useEffect(() => {
    API.get(`/api/frays/${frayId}`).then(r => {
      setFray(r.data);
      if (r.data.status === 'completed') {
        navigate(`/fray/${frayId}/results`);
      }
      if (r.data.status === 'stopped') {
        setShowStoppedOverlay(true);
      }
      if (r.data.status === 'paused') {
        setIsPaused(true);
      }
    }).catch(() => navigate('/dashboard'));
  }, [frayId, navigate]);

  const handleRerun = async () => {
    try {
      const res = await API.post(`/api/frays/${frayId}/rerun`);
      navigate(`/fray/${res.data.id}/reveal`);
    } catch (err) {
      console.error('Re-run failed:', err);
    }
  };

  // Start execution + SSE stream (durable architecture)
  useEffect(() => {
    if (!fray || streamingRef.current) return;
    if (fray.status === 'completed') { navigate(`/fray/${frayId}/results`); return; }
    if (fray.status === 'stopped') { setShowStoppedOverlay(true); return; }

    streamingRef.current = true;
    setIsStreaming(true);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('fray_token');
    let lastSeq = 0;

    const startAndStream = async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      // Step 1: Start the background runner (idempotent — safe to call multiple times)
      try {
        await fetch(`${backendUrl}/api/frays/${frayId}/start`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        });
      } catch (e) {
        console.warn('Start request failed (runner may already be active):', e.message);
      }

      // Step 2: Stream events with auto-reconnect
      let reconnectAttempts = 0;
      const MAX_RECONNECTS = 20;

      while (reconnectAttempts < MAX_RECONNECTS && !controller.signal.aborted) {
        try {
          const streamUrl = `${backendUrl}/api/frays/${frayId}/stream?after_seq=${lastSeq}`;
          const response = await fetch(streamUrl, {
            headers: { 'Authorization': `Bearer ${token}` },
            signal: controller.signal,
          });

          if (!response.ok) {
            const errText = await response.text();
            setError(errText);
            break;
          }

          reconnectAttempts = 0;
          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            let eventType = '';
            let eventData = '';
            let eventId = '';

            for (const line of lines) {
              if (line.startsWith('id: ')) {
                eventId = line.slice(4).trim();
              } else if (line.startsWith('event: ')) {
                eventType = line.slice(7).trim();
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6).trim();
              } else if (line.startsWith(':')) {
                // SSE comment (keepalive) — ignore
              } else if (line === '' && eventType && eventData) {
                try {
                  const data = JSON.parse(eventData);
                  handleSSEEvent(eventType, data);
                  if (eventId) lastSeq = parseInt(eventId) || lastSeq;

                  // Check for terminal events
                  if (['run_completed', 'run_failed', 'run_stopped'].includes(eventType)) {
                    if (eventType === 'run_completed' || eventType === 'run_stopped') {
                      setTimeout(() => navigate(`/fray/${frayId}/results`), 2000);
                    }
                    setIsStreaming(false);
                    streamingRef.current = false;
                    return;
                  }
                } catch (e) {
                  console.error('SSE parse error:', e);
                }
                eventType = '';
                eventData = '';
                eventId = '';
              }
            }
          }

          // Stream ended normally — check if fray completed
          const statusRes = await fetch(`${backendUrl}/api/frays/${frayId}`, { headers: { 'Authorization': `Bearer ${token}` } });
          const statusData = await statusRes.json();
          if (statusData.status === 'completed') {
            navigate(`/fray/${frayId}/results`);
            return;
          }
          if (statusData.status === 'stopped' || statusData.status === 'failed') {
            navigate(`/fray/${frayId}/results`);
            return;
          }
        } catch (err) {
          if (err.name === 'AbortError') return;
          console.warn(`SSE disconnected (reconnect ${reconnectAttempts + 1}/${MAX_RECONNECTS}, lastSeq=${lastSeq}):`, err.message);
        }

        reconnectAttempts++;
        await new Promise(r => setTimeout(r, 1000));
      }

      setIsStreaming(false);
      streamingRef.current = false;
    };

    startAndStream();
  }, [fray, frayId]);

  const handleSSEEvent = useCallback((type, data) => {
    // Capture ALL events for debug panel
    const ts = new Date().toISOString().slice(11, 23);
    setDebugLogs(prev => {
      const entry = { ts, type, summary: '' };
      if (type === 'voice_preparing') entry.summary = `${data.voice} | model=${data.model} | provider=${data.provider} | ~${data.estimated_input_tokens} tok`;
      else if (type === 'voice_thinking') entry.summary = `${data.voice} | waiting for response...`;
      else if (type === 'voice_message') entry.summary = `${data.voice} | status=${data.status}${data.text ? ' | ' + data.text.slice(0, 60) + '...' : ''}`;
      else if (type === 'voice_usage') entry.summary = `${data.voice} | ${data.tokens_prompt}/${data.tokens_completion} tok | gen_id=${data.generation_id || 'none'} | ${data.generation_time_ms}ms${data.cost_usd ? ' | $' + data.cost_usd.toFixed(4) : ''}`;
      else if (type === 'voice_error') entry.summary = `ERROR: ${data.voice} | ${data.code} | ${data.message?.slice(0, 100)}`;
      else if (type === 'voice_reasoning') entry.summary = `${data.voice} | ~${data.reasoning_tokens} reasoning tokens`;
      else if (type === 'voice_reasoning_start') entry.summary = `${data.voice} | reasoning stream started`;
      else if (type === 'voice_reasoning_chunk') entry.summary = `${data.voice_id} | +${data.text?.length || 0} chars: "${(data.text || '').slice(0, 80)}${data.text?.length > 80 ? '...' : ''}"`;
      else if (type === 'voice_reasoning_end') entry.summary = `${data.voice_id} | reasoning complete ~${data.reasoning_tokens} tokens`;
      else if (type === 'phase_transition') entry.summary = `Phase ${data.phase}: ${data.phase_name}`;
      else if (type === 'round_usage') entry.summary = `Round ${data.round_number} | ${data.voices_completed} voices | ${data.total_tokens_in + data.total_tokens_out} tok | running=$${data.fray_running_total_cost?.toFixed(4) || '0'}`;
      else if (type === 'tension_update') entry.summary = `score=${data.score} | ${data.classification}`;
      else if (type === 'verdict') entry.summary = `${data.verdict} (${data.verdict_confidence})`;
      else if (type === 'heartbeat') entry.summary = `status=${data.status}`;
      else if (type === 'error') entry.summary = `${data.code}: ${data.message?.slice(0, 100)}`;
      else if (type === 'typing_indicator') entry.summary = `${data.voice} | ${data.status}`;
      else if (type === 'fray_usage') entry.summary = `FINAL: ${data.total_tokens} tok | $${data.total_cost_usd?.toFixed(4) || '0'} | ${data.total_time_ms}ms`;
      else if (type === 'render_start') entry.summary = `Generating ${data.spec_count || '?'} assets (phase ${data.phase || '?'})`;
      else if (type === 'render_complete') entry.summary = `${data.asset_count || '?'} assets rendered (phase ${data.phase || '?'})`;
      else entry.summary = JSON.stringify(data).slice(0, 100);
      return [...prev.slice(-200), entry]; // keep last 200
    });

    switch (type) {
      case 'phase_transition':
        setCurrentPhase(data);
        setMessages(prev => [...prev, { type: 'phase', data }]);
        break;

      case 'voice_preparing':
        setActiveVoice(data.voice);
        setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'preparing' }));
        setVoiceStats(prev => ({ ...prev, [data.voice_id]: { status: 'preparing', model: data.model, estimated_tokens: data.estimated_input_tokens } }));
        setMessages(prev => [...prev, { type: 'preparing', data }]);
        break;

      case 'voice_thinking':
        setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'thinking' }));
        setVoiceStats(prev => ({ ...prev, [data.voice_id]: { ...prev[data.voice_id], status: 'thinking', elapsed_ms: data.elapsed_ms } }));
        break;

      case 'typing_indicator':
        if (data.status === 'thinking') {
          setActiveVoice(data.voice);
          setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'thinking' }));
        } else if (data.status === 'idle') {
          setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'idle' }));
          if (activeVoice === data.voice) setActiveVoice(null);
        }
        break;

      case 'voice_message':
        if (data.status === 'started') {
          setActiveVoice(data.voice);
          setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'speaking' }));
        } else if (data.status === 'streaming') {
          setMessages(prev => [...prev, { type: 'message', data }]);
        } else if (data.status === 'complete') {
          setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'complete' }));
        }
        break;

      case 'voice_started':
        setActiveVoice(data.voice);
        setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'speaking' }));
        break;

      case 'voice_complete':
        setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'complete' }));
        if (activeVoice === data.voice) setActiveVoice(null);
        break;

      case 'run_started':
        break;
      case 'run_completed':
        setIsStreaming(false);
        break;
      case 'run_failed':
        setError(data.error || 'Fray execution failed');
        setIsStreaming(false);
        break;
      case 'run_stopped':
        setIsStreaming(false);
        setTimeout(() => navigate(`/fray/${frayId}/results`), 2000);
        break;

      case 'tension_update':
        setTension({ score: data.score, classification: data.classification });
        break;

      case 'heartbeat':
        if (data.status === 'paused') {
          setIsPaused(true);
        }
        break;

      case 'verdict':
        setVerdict(data);
        setMessages(prev => [...prev, { type: 'verdict', data }]);
        setTimeout(() => navigate(`/fray/${frayId}/results`), 3000);
        break;

      case 'error':
        if (data.code === 'FRAY_STOPPED') {
          setIsStreaming(false);
          streamingRef.current = false;
          setMessages(prev => [...prev, { type: 'stopped', data }]);
          setTimeout(() => navigate(`/fray/${frayId}/results`), 2000);
        } else {
          setMessages(prev => [...prev, { type: 'error', data }]);
        }
        break;

      case 'voice_usage':
        setVoiceStats(prev => ({ ...prev, [data.voice_id]: {
          status: 'done', tokens_prompt: data.tokens_prompt, tokens_completion: data.tokens_completion,
          cost_usd: data.cost_usd, generation_time_ms: data.generation_time_ms, model: data.model,
        }}));
        // Clear the spinner state
        setVoiceStates(prev => ({ ...prev, [data.voice_id]: 'complete' }));
        if (activeVoice === data.voice) setActiveVoice(null);
        setMessages(prev => [...prev, { type: 'voice_complete_stats', data }]);
        break;

      case 'round_usage':
        setRoundUsage(prev => [...prev, data]);
        setFrayTotalCost(data.fray_running_total_cost || 0);
        setFrayTotalTokens(data.fray_running_total_tokens || 0);
        setMessages(prev => [...prev, { type: 'round_stats', data }]);
        break;

      case 'fray_usage':
        setFrayTotalCost(data.total_cost_usd || 0);
        setFrayTotalTokens(data.total_tokens || 0);
        break;

      case 'voice_reasoning':
        setMessages(prev => [...prev, { type: 'reasoning', data }]);
        break;

      case 'voice_reasoning_start':
        setMessages(prev => [...prev, { type: 'reasoning_stream_start', data }]);
        break;

      case 'voice_reasoning_chunk':
        // Append chunk to the last reasoning_stream message
        setMessages(prev => {
          const last = prev[prev.length - 1];
          if (last && last.type === 'reasoning_stream' && last.data.voice_id === data.voice_id) {
            return [...prev.slice(0, -1), { ...last, data: { ...last.data, text: last.data.text + data.text } }];
          }
          if (last && last.type === 'reasoning_stream_start' && last.data.voice_id === data.voice_id) {
            return [...prev.slice(0, -1), { type: 'reasoning_stream', data: { ...last.data, text: data.text } }];
          }
          return [...prev, { type: 'reasoning_stream', data: { ...data, text: data.text } }];
        });
        break;

      case 'voice_reasoning_end':
        setMessages(prev => {
          const updated = prev.map(m => {
            if (m.type === 'reasoning_stream' && m.data.voice_id === data.voice_id) {
              return { ...m, data: { ...m.data, complete: true, reasoning_tokens: data.reasoning_tokens } };
            }
            return m;
          });
          return updated;
        });
        break;

      case 'voice_error':
        // Voice failed — show model picker dialog
        setVoiceError({
          voice: data.voice,
          voice_id: data.voice_id,
          model: data.model,
          message: data.message,
          suggestion: data.suggestion,
        });
        setIsPaused(true);
        break;

      // Render events (asset generation) — both sidebar + inline stream
      case 'render_start':
        setRenderEvents(prev => [...prev, { type: 'render_start', spec_count: data.spec_count, phase: data.phase }]);
        setMessages(prev => [...prev, { type: 'render_start', data }]);
        break;
      case 'render_complete':
        setRenderEvents(prev => [...prev, { type: 'render_complete', asset_count: data.asset_count, phase: data.phase }]);
        setMessages(prev => [...prev, { type: 'render_complete', data }]);
        break;

      // Cat Fight events
      case 'heckle_submitted':
      case 'provocation_submitted':
        setMessages(prev => [...prev, { type: 'heckle', data: { ...data, event_type: type } }]);
        break;
      case 'heckle_delivered':
        setMessages(prev => [...prev, { type: 'heckle_delivered', data }]);
        break;
      case 'hot_moment':
        setMessages(prev => [...prev, { type: 'hot_moment', data }]);
        break;
      case 'hot_seat':
        setMessages(prev => [...prev, { type: 'hot_seat', data }]);
        break;
      case 'budget_exhausted':
        setMessages(prev => [...prev, { type: 'budget_exhausted', data }]);
        break;

      default:
        break;
    }
  }, [activeVoice, frayId, navigate]);

  // Auto-scroll — scroll to bottom on new messages, with a slight delay for render
  useEffect(() => {
    const timer = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(timer);
  }, [messages, voiceStats]);

  // Auto-scroll debug panel
  useEffect(() => {
    if (showDebugPanel) {
      debugEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [debugLogs, showDebugPanel]);

  // Elapsed timer
  useEffect(() => {
    if (!isStreaming) return;
    const interval = setInterval(() => setElapsed(e => e + 1), 1000);
    return () => clearInterval(interval);
  }, [isStreaming]);

  if (!fray) return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const voices = fray.voices || [];
  const formatTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const isCatFight = fray.debate_mode === 'cat_fight';

  // Cat Fight mode — delegate to dedicated themed component
  if (isCatFight) {
    return (
      <CatFightTheater
        fray={fray} messages={messages} voices={voices}
        activeVoice={activeVoice} currentPhase={currentPhase} tension={tension}
        verdict={verdict} error={error}
        isStreaming={isStreaming} isPaused={isPaused} elapsed={elapsed}
        frayTotalCost={frayTotalCost} frayTotalTokens={frayTotalTokens}
        showStoppedOverlay={showStoppedOverlay}
        onStop={handleStop} onPause={handlePause} onResume={handleResume}
        onRerun={handleRerun}
        onNavigateResults={() => navigate(`/fray/${frayId}/results`)}
        onNavigateDashboard={() => navigate('/dashboard')}
        frayId={frayId}
      />
    );
  }
  const catTheme = fray.cat_fight_theme || 'boxing_ring';

  // Theme color schemes
  const THEME_COLORS = {
    boxing_ring: { accent: '#EF4444', bg: '#0A0A0A', border: '#3A1111', label: 'BOXING RING', headerBg: 'bg-gradient-to-r from-[#1A0505] to-[#0F0F0F]' },
    live_stream: { accent: '#A855F7', bg: '#0F0F0F', border: '#2A1A3A', label: 'LIVE STREAM', headerBg: 'bg-gradient-to-r from-[#1A0A2A] to-[#0F0F0F]' },
    wrestling: { accent: '#F59E0B', bg: '#0F0F0F', border: '#3A2A0A', label: 'WRESTLING', headerBg: 'bg-gradient-to-r from-[#1A1500] to-[#0F0F0F]' },
    default: { accent: '#FF4D00', bg: '#0F0F0F', border: '#2A2A2A', label: '', headerBg: '' },
  };
  const theme = isCatFight ? (THEME_COLORS[catTheme] || THEME_COLORS.boxing_ring) : THEME_COLORS.default;
  const accentColor = theme.accent;

  return (
    <div className="h-screen bg-[#0F0F0F] flex flex-col overflow-hidden" data-testid="theater">
      {/* Theater Header */}
      <div className={`h-12 border-b flex items-center justify-between px-2 md:px-4 shrink-0 ${isCatFight ? theme.headerBg : 'glass'}`}
        style={{ borderColor: isCatFight ? theme.border : '#2A2A2A' }}>
        <div className="flex items-center gap-1.5 md:gap-3 min-w-0">
          <div className="flex items-center gap-1 md:gap-1.5 shrink-0">
            <div className={`w-2 h-2 rounded-full ${isPaused ? 'bg-[#F59E0B]' : 'animate-pulse'}`}
              style={{ backgroundColor: isPaused ? '#F59E0B' : accentColor }} />
            <span className="text-[10px] md:text-xs font-mono" style={{ color: isPaused ? '#F59E0B' : accentColor }}>
              {isPaused ? 'PAUSED' : isCatFight ? 'FIGHT' : 'LIVE'}
            </span>
          </div>
          <span className="text-xs md:text-sm font-['Barlow_Condensed'] font-medium truncate">{fray.template_name}</span>
          {isCatFight && (
            <span className="text-[8px] px-1.5 py-0.5 border font-mono font-bold hidden sm:inline"
              style={{ color: accentColor, borderColor: accentColor + '40' }}>{theme.label}</span>
          )}
        </div>
        <div className="flex items-center gap-2 md:gap-4 shrink-0">
          <span className="text-xs font-mono text-[#A0A0A0]"><Clock className="w-3 h-3 inline mr-1" />{formatTime(elapsed)}</span>
          {frayTotalTokens > 0 && (
            <span className="hidden md:inline text-[10px] font-mono text-[#22C55E] bg-[#22C55E]/10 px-2 py-0.5 border border-[#22C55E]/20">
              {frayTotalTokens.toLocaleString()} tokens{frayTotalCost > 0 ? ` / $${frayTotalCost.toFixed(4)}` : ''}
            </span>
          )}
          {fray?.share_token && (
            <button
              onClick={() => {
                const shareUrl = `${window.location.origin}/share/${fray.share_token}`;
                navigator.clipboard.writeText(shareUrl);
                setShareCopied(true);
                setTimeout(() => setShareCopied(false), 2000);
              }}
              className="flex items-center gap-1 px-2 py-1 min-h-[44px] md:min-h-0 text-xs font-mono text-[#A0A0A0] hover:text-[#FF4D00] border border-[#2A2A2A] hover:border-[#FF4D00]/30 transition-colors"
              title="Share watch party link"
              data-testid="share-watch-party"
            >
              {shareCopied ? <><Check className="w-3 h-3 text-[#22C55E]" /><span className="hidden md:inline text-[#22C55E]">Copied!</span></> : <><Share2 className="w-3 h-3" /><span className="hidden md:inline">Share</span></>}
            </button>
          )}
          <button onClick={() => setShowDebugPanel(!showDebugPanel)}
            className={`hidden md:flex p-1 transition-colors ${showDebugPanel ? 'text-[#22C55E]' : 'text-[#2A2A2A] hover:text-[#A0A0A0]'}`}
            title="Debug Panel" data-testid="debug-toggle">
            <Terminal className="w-4 h-4" />
          </button>
          {currentPhase && (
            <span className="hidden md:inline text-xs font-mono text-[#FF4D00]">
              Phase {currentPhase.phase}/{currentPhase.total_phases}: {currentPhase.phase_name}
            </span>
          )}
          {isStreaming && !isPaused && (
            <div className="flex items-center gap-1.5">
              <button
                onClick={handlePause}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 gap-1 px-2 py-1 md:px-2.5 border border-[#F59E0B]/40 text-[#F59E0B] text-xs font-mono hover:bg-[#F59E0B]/10 transition-colors"
                data-testid="pause-fray-btn"
              >
                <Pause className="w-3 h-3" /> <span className="hidden md:inline">Pause</span>
              </button>
              <button
                onClick={handleStop}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 gap-1 px-2 py-1 md:px-2.5 bg-[#EF4444] text-white text-xs font-mono hover:bg-[#EF4444]/80 transition-colors"
                data-testid="stop-fray-btn"
              >
                <Square className="w-3 h-3" /> <span className="hidden md:inline">Stop</span>
              </button>
            </div>
          )}
          {isPaused && (
            <div className="flex items-center gap-1.5">
              <span className="hidden md:inline text-[9px] font-mono text-[#F59E0B] animate-pulse">PAUSED</span>
              <button
                onClick={handleResume}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 gap-1 px-2 py-1 md:px-2.5 bg-[#22C55E] text-black text-xs font-mono hover:bg-[#22C55E]/80 transition-colors"
                data-testid="resume-fray-btn"
              >
                <Play className="w-3 h-3" /> <span className="hidden md:inline">Resume</span>
              </button>
              <button
                onClick={handleStop}
                className="flex items-center justify-center min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 gap-1 px-2 py-1 md:px-2.5 bg-[#EF4444] text-white text-xs font-mono hover:bg-[#EF4444]/80 transition-colors"
                data-testid="stop-fray-btn-paused"
              >
                <Square className="w-3 h-3" /> <span className="hidden md:inline">Stop</span>
              </button>
            </div>
          )}
          {!isStreaming && !isPaused && verdict && (
            <button
              onClick={() => navigate(`/fray/${frayId}/results`)}
              className="flex items-center gap-1.5 px-3 py-1 min-h-[44px] md:min-h-0 bg-[#FF4D00] text-black text-xs font-medium"
              data-testid="view-results-btn"
            >
              View Results <ArrowRight className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Voice Rail */}
      <div className="h-16 md:h-24 border-b border-[#2A2A2A] flex items-center gap-2 md:gap-3 px-2 md:px-4 overflow-x-auto shrink-0" data-testid="voice-rail">
        {voices.map((voice, i) => {
          const vid = voice.name.toLowerCase().replace(/[\s']/g, '-');
          const state = voiceStates[vid] || 'idle';
          const isActive = activeVoice === voice.name;
          const stats = voiceStats[vid];

          return (
            <div
              key={i}
              className={`flex items-center gap-1.5 md:gap-2.5 px-2 py-1.5 md:px-3 md:py-2 border transition-all shrink-0 ${
                isActive ? 'border-[#FF4D00] bg-[#FF4D00]/5' :
                state === 'complete' || stats?.status === 'done' ? 'border-[#22C55E]/30' :
                'border-[#2A2A2A]'
              }`}
              data-testid={`voice-rail-card-${i}`}
            >
              <div className="relative shrink-0">
                <VoiceAvatar
                  name={voice.name}
                  color={voice.color}
                  emotion={
                    stats?.status === 'done' ? 'idle' :
                    state === 'speaking' ? 'speaking' :
                    (state === 'thinking' || state === 'preparing') ? 'thinking' :
                    'idle'
                  }
                  alteredState={voice.altered_state || null}
                  size={window.innerWidth < 768 ? 28 : 36}
                />
                {(state === 'thinking' || state === 'preparing') && stats?.status !== 'done' && (
                  <div className="absolute -bottom-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-[#FF4D00] rounded-full animate-pulse" />
                )}
                {state === 'speaking' && stats?.status !== 'done' && (
                  <div className="absolute -bottom-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-[#FF4D00] rounded-full">
                    <div className="w-full h-full bg-[#FF4D00] rounded-full animate-ping" />
                  </div>
                )}
                {stats?.status === 'done' && (
                  <div className="absolute -bottom-1 -right-1 w-2 h-2 md:w-3 md:h-3 bg-[#22C55E] rounded-full" />
                )}
              </div>
              <div className="min-w-0">
                <span className="text-[10px] md:text-xs font-medium font-['Barlow_Condensed'] block truncate max-w-[60px] md:max-w-[100px]">{voice.name}</span>
                {/* Observability status line — hidden on mobile, show only status dot via avatar */}
                <span className="hidden md:block">
                  {stats?.status === 'preparing' && (
                    <span className="text-[9px] font-mono text-[#A0A0A0] block">preparing ~{stats.estimated_tokens?.toLocaleString()} tok</span>
                  )}
                  {stats?.status === 'thinking' && (
                    <span className="text-[9px] font-mono text-[#FF4D00] block animate-pulse">generating...</span>
                  )}
                  {stats?.status === 'done' ? (
                    <span className="leading-tight">
                      <span className="text-[9px] font-mono text-[#22C55E] block">
                        {((stats.tokens_prompt || 0) + (stats.tokens_completion || 0)).toLocaleString()} tok
                        {stats.generation_time_ms ? ` ${(stats.generation_time_ms/1000).toFixed(1)}s` : ''}
                      </span>
                      {stats.cost_usd > 0 && <span className="text-[8px] font-mono text-[#FF4D00] block">${stats.cost_usd.toFixed(4)}</span>}
                    </span>
                  ) : !stats && (
                    <span className="text-[9px] font-mono flex items-center gap-1" style={{ color: TIER_COLORS[voice.model_tier] }}>
                      {voice.default_model?.split('/').pop() || voice.model_tier}
                      {isVisionCapable(voice.default_model) && (
                        <span className="inline-flex items-center gap-0.5 px-1 py-px bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#3B82F6] text-[7px]" title="Vision-capable model" data-testid={`vision-badge-${i}`}>
                          <Eye className="w-2 h-2" />
                        </span>
                      )}
                    </span>
                  )}
                </span>
                {/* Mobile: show minimal status */}
                <span className="md:hidden">
                  {stats?.status === 'thinking' && (
                    <span className="text-[8px] font-mono text-[#FF4D00] animate-pulse">...</span>
                  )}
                  {stats?.status === 'done' && (
                    <span className="text-[8px] font-mono text-[#22C55E]">done</span>
                  )}
                  {!stats && (
                    <span className="text-[8px] font-mono flex items-center gap-0.5" style={{ color: TIER_COLORS[voice.model_tier] }}>
                      {voice.model_tier}
                      {isVisionCapable(voice.default_model) && (
                        <Eye className="w-2 h-2 text-[#3B82F6]" />
                      )}
                    </span>
                  )}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Theater Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Debug Panel — slides in from left, full-width overlay on mobile */}
        {showDebugPanel && (
          <div className="absolute inset-0 md:relative md:inset-auto w-full md:w-96 border-r border-[#2A2A2A] flex flex-col shrink-0 bg-[#0A0A0A] z-40" data-testid="debug-panel">
            <div className="h-11 md:h-8 flex items-center justify-between px-3 border-b border-[#2A2A2A] shrink-0">
              <div className="flex items-center gap-2">
                <Terminal className="w-3 h-3 text-[#22C55E]" />
                <span className="text-[9px] font-mono text-[#22C55E]">DEBUG TELEMETRY</span>
                <span className="text-[8px] font-mono text-[#A0A0A0]">{debugLogs.length} events</span>
              </div>
              <button onClick={() => setShowDebugPanel(false)} className="min-h-[44px] min-w-[44px] flex items-center justify-center text-[#A0A0A0] hover:text-[#F5F5F5]">
                <XIcon className="w-4 h-4 md:w-3 md:h-3" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto font-mono text-[9px] leading-relaxed">
              {debugLogs.map((log, i) => {
                const isError = log.type === 'voice_error' || log.type === 'error';
                const isSuccess = log.type === 'voice_usage' || log.type === 'fray_usage';
                const isCall = log.type === 'voice_preparing' || log.type === 'voice_thinking';
                const isPhase = log.type === 'phase_transition' || log.type === 'round_usage';
                return (
                  <div key={i} className={`px-2 py-1 border-b border-[#1A1A1A] ${
                    isError ? 'bg-[#EF4444]/5' : isSuccess ? 'bg-[#22C55E]/5' : isCall ? 'bg-[#FF4D00]/5' : isPhase ? 'bg-[#3B82F6]/5' : ''
                  }`}>
                    <div className="flex items-start gap-1.5">
                      <span className="text-[#A0A0A0] shrink-0">{log.ts}</span>
                      <span className={`shrink-0 px-1 ${
                        isError ? 'text-[#EF4444]' : isSuccess ? 'text-[#22C55E]' : isCall ? 'text-[#FF4D00]' : isPhase ? 'text-[#3B82F6]' : 'text-[#A0A0A0]'
                      }`}>{log.type}</span>
                    </div>
                    <div className="text-[#E0E0E0] pl-16 mt-0.5 break-all">{log.summary}</div>
                  </div>
                );
              })}
              <div ref={debugEndRef} />
            </div>
          </div>
        )}

        {/* Argument Stream */}
        <div className="flex-1 overflow-y-auto px-3 py-3 md:px-6 md:py-4" data-testid="argument-stream">
          {messages.length === 0 && isStreaming && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <div className="w-8 h-8 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-[#A0A0A0] text-sm font-mono">Preparing the committee...</p>
              </div>
            </div>
          )}

          <AnimatePresence>
            {messages.map((msg, i) => {
              if (msg.type === 'preparing') {
                const voice = voices.find(v => v.name === msg.data.voice);
                return (
                  <motion.div key={`prep-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 0.7 }}
                    className="mb-2 px-3 md:px-4 py-2 flex flex-wrap items-center gap-2">
                    <div className="w-4 h-4 border border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
                    <span className="text-xs font-mono" style={{ color: voice?.color || '#A0A0A0' }}>{msg.data.voice}</span>
                    <span className="text-[10px] text-[#A0A0A0] font-mono hidden md:inline">generating with {msg.data.model} (~{msg.data.estimated_input_tokens?.toLocaleString()} input tokens)</span>
                    <span className="text-[10px] text-[#A0A0A0] font-mono md:hidden">generating...</span>
                  </motion.div>
                );
              }

              if (msg.type === 'voice_complete_stats') {
                const voice = voices.find(v => v.name === msg.data.voice);
                const totalTok = (msg.data.tokens_prompt || 0) + (msg.data.tokens_completion || 0);
                return (
                  <motion.div key={`vstats-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="mb-3 px-3 md:px-4 py-1.5 flex flex-wrap items-center gap-2 md:gap-3 border-l-2" style={{ borderColor: voice?.color || '#2A2A2A' }}>
                    <span className="text-[10px] font-mono" style={{ color: voice?.color }}>{msg.data.voice}</span>
                    <span className="text-[10px] font-mono text-[#22C55E]">{totalTok.toLocaleString()} tok<span className="hidden md:inline">ens ({msg.data.tokens_prompt?.toLocaleString()} in / {msg.data.tokens_completion?.toLocaleString()} out)</span></span>
                    {msg.data.generation_time_ms > 0 && <span className="text-[10px] font-mono text-[#A0A0A0]">{(msg.data.generation_time_ms / 1000).toFixed(1)}s</span>}
                    {msg.data.cost_usd > 0 && <span className="text-[10px] font-mono text-[#FF4D00]">${msg.data.cost_usd.toFixed(4)}</span>}
                  </motion.div>
                );
              }

              if (msg.type === 'reasoning') {
                const voice = voices.find(v => v.name === msg.data.voice);
                return <ReasoningBlock key={`reason-${i}`} voice={voice} data={msg.data} />;
              }

              if (msg.type === 'reasoning_stream' || msg.type === 'reasoning_stream_start') {
                const voice = voices.find(v => v.name === msg.data.voice);
                return <StreamingReasoningBlock key={`sreason-${i}`} voice={voice} data={msg.data} />;
              }

              if (msg.type === 'round_stats') {
                return (
                  <motion.div key={`rstats-${i}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    className="my-4 mx-auto max-w-md mx-4 md:mx-auto p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
                    <span className="text-[9px] font-mono text-[#A0A0A0] block mb-1">ROUND {msg.data.round_number} COMPLETE</span>
                    <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-4 text-[10px] font-mono">
                      <span className="text-[#22C55E]">{(msg.data.total_tokens_in + msg.data.total_tokens_out).toLocaleString()} tokens</span>
                      <span className="text-[#A0A0A0]">{msg.data.voices_completed} voices</span>
                      {msg.data.total_cost_usd > 0 && <span className="text-[#FF4D00]">${msg.data.total_cost_usd.toFixed(4)}</span>}
                    </div>
                    <div className="text-[9px] font-mono text-[#A0A0A0] mt-1">Running total: {msg.data.fray_running_total_tokens?.toLocaleString()} tokens</div>
                  </motion.div>
                );
              }

              if (msg.type === 'phase') {
                const isCatFightRound = msg.data.transition_type === 'cat_fight_round';
                const wrestlingAnnounce = isCatFight && catTheme === 'wrestling';
                return (
                  <motion.div
                    key={`phase-${i}`}
                    initial={{ opacity: 0, scale: wrestlingAnnounce ? 1.2 : 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: wrestlingAnnounce ? 0.6 : 0.42 }}
                    className={`my-8 text-center ${wrestlingAnnounce ? 'py-4' : ''}`}
                    data-testid={`phase-transition-${msg.data.phase}`}
                  >
                    {wrestlingAnnounce ? (
                      <div className="inline-block px-4 py-3 md:px-8 md:py-4 bg-gradient-to-r from-[#F59E0B]/10 via-[#F59E0B]/20 to-[#F59E0B]/10 border-2 border-[#F59E0B]/50">
                        <span className="text-[10px] font-mono text-[#F59E0B] block mb-1">AND NOW...</span>
                        <span className="text-base md:text-xl font-bold font-['Barlow_Condensed'] text-[#F59E0B]">
                          {msg.data.phase_name?.toUpperCase()} — FIGHT!
                        </span>
                      </div>
                    ) : (
                    <div className="inline-flex items-center gap-2 px-3 py-2 md:px-6 md:py-3 bg-[#1A1A1A] border"
                      style={{ borderColor: isCatFight ? accentColor + '40' : '#FF4D0040' }}>
                      <ChevronRight className="w-4 h-4" style={{ color: accentColor }} />
                      <span className="text-xs md:text-sm font-['Barlow_Condensed'] font-semibold">
                        {isCatFightRound ? msg.data.phase_name : `Phase ${msg.data.phase}: ${msg.data.phase_name}`}
                      </span>
                      {msg.data.transition_type === 'final_call' && (
                        <Zap className="w-4 h-4 text-[#FF4D00]" />
                      )}
                    </div>
                    )}
                    {!wrestlingAnnounce && <p className="text-xs text-[#A0A0A0] mt-2 max-w-md mx-auto">{msg.data.message}</p>}
                  </motion.div>
                );
              }

              if (msg.type === 'message') {
                const voice = voices.find(v => v.name === msg.data.voice);
                const isHotTake = msg.data.moment_tags?.includes('hot_take') || msg.data.moment_tags?.includes('hot_moment');
                const isLiveStream = isCatFight && catTheme === 'live_stream';
                const isWrestling = isCatFight && catTheme === 'wrestling';

                // Live Stream theme: compact chat bubbles
                if (isLiveStream) {
                  return (
                    <motion.div key={`msg-${i}`} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.08 }}
                      className={`mb-1 px-3 py-2 flex gap-2 items-start ${isHotTake ? 'bg-[#A855F7]/10' : ''}`}
                      data-testid={`argument-card-${i}`}>
                      <span className="text-xs font-bold shrink-0" style={{ color: voice?.color || '#A0A0A0' }}>
                        {msg.data.voice}:
                      </span>
                      <p className="text-sm text-[#F5F5F5] leading-snug flex-1">{msg.data.text}</p>
                      {isHotTake && <span className="text-xs shrink-0">🔥</span>}
                    </motion.div>
                  );
                }

                // Wrestling theme: dramatic entrance
                if (isWrestling) {
                  return (
                    <motion.div key={`msg-${i}`}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, type: 'spring', stiffness: 200 }}
                      className={`mb-3 p-3 md:p-4 border-l-4 bg-[#1A1A1A] border transition-all ${isHotTake ? 'bg-[#F59E0B]/5' : ''}`}
                      style={{ borderLeftColor: voice?.color || '#F59E0B', borderColor: isHotTake ? '#F59E0B40' : '#2A2A2A' }}
                      data-testid={`argument-card-${i}`}>
                      {isHotTake && (
                        <div className="text-center mb-2">
                          <span className="text-xs font-bold text-[#F59E0B] font-mono animate-pulse">💥 DEVASTATING! 💥</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-bold font-['Barlow_Condensed'] uppercase" style={{ color: voice?.color }}>{msg.data.voice}</span>
                        <span className="text-[9px] font-mono text-[#A0A0A0]">{msg.data.model_tier}</span>
                      </div>
                      <p className="text-sm text-[#F5F5F5] leading-relaxed whitespace-pre-wrap">{msg.data.text}</p>
                      <div className="flex items-center justify-between mt-1">
                        <ReactionBar frayId={frayId} voiceId={msg.data.voice_id} sequenceId={i} />
                        <ClipButton frayId={frayId} voice={msg.data.voice} phase={msg.data.phase} text={msg.data.text} sequenceId={i} />
                      </div>
                    </motion.div>
                  );
                }

                // #36: Parse render_specs from Art Director messages
                const renderSpecsMatch = msg.data.text?.match(/<render_specs>([\s\S]*?)<\/render_specs>/);
                let renderSpecs = null;
                let displayText = msg.data.text;
                if (renderSpecsMatch) {
                  try { renderSpecs = JSON.parse(renderSpecsMatch[1].trim()); } catch {}
                  // Strip the raw tag from displayed text
                  displayText = msg.data.text.replace(/<render_specs>[\s\S]*?<\/render_specs>/, '').trim();
                }

                // Default / Boxing Ring theme: standard cards with accent color
                return (
                  <motion.div
                    key={`msg-${i}`}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.12 }}
                    className={`mb-3 p-3 md:p-4 bg-[#1A1A1A] border transition-all ${
                      isHotTake ? `border-[${accentColor}]/50 bg-[${accentColor}]/5` : 'border-[#2A2A2A]'
                    }`}
                    style={isHotTake && isCatFight ? { borderColor: accentColor + '80', backgroundColor: accentColor + '0D' } : {}}
                    data-testid={`argument-card-${i}`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 flex items-center justify-center border text-[8px] font-bold font-['Barlow_Condensed']"
                        style={{ borderColor: voice?.color || '#A0A0A0', color: voice?.color || '#A0A0A0' }}>
                        {msg.data.voice?.charAt(0)}
                      </div>
                      <span className="text-xs font-medium font-['Barlow_Condensed']" style={{ color: voice?.color }}>{msg.data.voice}</span>
                      {isHotTake && (
                        <span className="text-[9px] px-1.5 py-0.5 border font-mono flex items-center gap-1"
                          style={{ backgroundColor: accentColor + '1A', color: accentColor, borderColor: accentColor + '40' }}>
                          <Zap className="w-3 h-3" /> {isCatFight ? 'SAVAGE' : 'HOT TAKE'}
                        </span>
                      )}
                      {isVisionCapable(voice?.default_model) && (
                        <span className="text-[7px] px-1 py-px bg-[#3B82F6]/10 border border-[#3B82F6]/30 text-[#3B82F6] font-mono flex items-center gap-0.5">
                          <Eye className="w-2 h-2" /> VISION
                        </span>
                      )}
                      <span className="text-[9px] font-mono text-[#A0A0A0] ml-auto">{msg.data.model_tier}</span>
                    </div>
                    <p className="text-sm text-[#F5F5F5] leading-relaxed whitespace-pre-wrap">{displayText}</p>
                    {/* #36: Render specs display */}
                    {renderSpecs && Array.isArray(renderSpecs) && renderSpecs.length > 0 && (
                      <div className="mt-3 p-2 bg-[#3B82F6]/5 border border-[#3B82F6]/20" data-testid="render-specs-display">
                        <span className="text-[9px] font-mono text-[#3B82F6] block mb-1.5 flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" /> RENDER SPECS ({renderSpecs.length})
                        </span>
                        <div className="space-y-1">
                          {renderSpecs.map((spec, si) => (
                            <div key={si} className="flex items-start gap-2 text-[10px]">
                              <span className="font-mono text-[#3B82F6] shrink-0">{spec.spec_id || `#${si+1}`}</span>
                              <span className="text-[#A0A0A0] flex-1">{spec.brief}</span>
                              {spec.aspect_ratio && <span className="font-mono text-[#A0A0A0] shrink-0">{spec.aspect_ratio}</span>}
                              {spec.type && <span className="font-mono text-[#A0A0A0] shrink-0">{spec.type}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <ReactionBar frayId={frayId} voiceId={msg.data.voice_id} sequenceId={i} />
                      <ClipButton frayId={frayId} voice={msg.data.voice} phase={msg.data.phase} text={msg.data.text} sequenceId={i} />
                    </div>
                  </motion.div>
                );
              }

              if (msg.type === 'verdict') {
                const VERDICT_COLORS = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };
                return (
                  <motion.div
                    key={`verdict-${i}`}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.42, type: 'spring' }}
                    className="my-8 p-4 md:p-6 bg-[#1A1A1A] border-2 text-center mx-4 md:mx-0"
                    style={{ borderColor: VERDICT_COLORS[msg.data.verdict] || '#FF4D00' }}
                    data-testid="verdict-display"
                  >
                    <span className="text-xs font-mono text-[#A0A0A0] block mb-2">FINAL CALL</span>
                    <span className="text-2xl md:text-4xl font-bold font-['Barlow_Condensed'] block mb-3"
                      style={{ color: VERDICT_COLORS[msg.data.verdict] || '#FF4D00' }}>
                      {msg.data.verdict}
                    </span>
                    <p className="text-sm text-[#A0A0A0] max-w-md mx-auto">{msg.data.executive_summary}</p>
                  </motion.div>
                );
              }

              if (msg.type === 'error') {
                return (
                  <motion.div key={`err-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="mb-3 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-[#EF4444] shrink-0 mt-0.5" />
                    <div>
                      <span className="text-xs font-mono text-[#EF4444]">{msg.data.code}</span>
                      <p className="text-sm text-[#F5F5F5] mt-1">{msg.data.message}</p>
                    </div>
                  </motion.div>
                );
              }

              // Cat Fight: Heckle messages
              if (msg.type === 'heckle') {
                const isProvocation = msg.data.event_type === 'provocation_submitted';
                return (
                  <motion.div key={`heckle-${i}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}
                    className="my-2 mx-4 p-3 bg-[#EF4444]/10 border border-[#EF4444]/30 flex items-start gap-2">
                    <span className="text-lg">📢</span>
                    <div>
                      <span className="text-[9px] font-mono text-[#EF4444]">
                        {isProvocation ? `${msg.data.author} → ${msg.data.target}` : `${msg.data.author} → EVERYONE`}
                      </span>
                      <p className="text-sm text-[#F5F5F5] mt-0.5">"{msg.data.text}"</p>
                    </div>
                  </motion.div>
                );
              }

              if (msg.type === 'hot_moment') {
                return (
                  <motion.div key={`hot-${i}`} initial={{ opacity: 0, scale: 1.1 }} animate={{ opacity: 1, scale: 1 }}
                    className="my-1 mx-4 p-2 bg-[#EF4444]/5 border-l-2 border-[#EF4444] text-[10px] font-mono text-[#EF4444]">
                    🔥 {msg.data.voice}: "{msg.data.text_snippet}"
                  </motion.div>
                );
              }

              if (msg.type === 'budget_exhausted') {
                return (
                  <motion.div key={`budget-${i}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="my-4 p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-center">
                    <span className="text-sm font-['Barlow_Condensed'] font-medium text-[#F59E0B]">Budget Exhausted</span>
                    <p className="text-xs text-[#A0A0A0] mt-1">Spent ${msg.data.total_spent?.toFixed(4)} of ${msg.data.budget_limit} limit</p>
                  </motion.div>
                );
              }

              if (msg.type === 'stopped') {
                return (
                  <motion.div key={`stop-${i}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="my-6 p-6 bg-[#1A1A1A] border border-[#A0A0A0]/30 text-center">
                    <Square className="w-6 h-6 text-[#A0A0A0] mx-auto mb-2" />
                    <span className="text-sm font-['Barlow_Condensed'] font-medium block mb-1">Fray Stopped</span>
                    <p className="text-xs text-[#A0A0A0] mb-4">The debate was stopped before completion.</p>
                    <button onClick={() => navigate('/dashboard')}
                      className="px-4 py-1.5 bg-[#FF4D00] text-black text-xs font-medium" data-testid="back-after-stop">
                      Back to Dashboard
                    </button>
                  </motion.div>
                );
              }

              {/* #35: Inline render progress during fray execution */}
              if (msg.type === 'render_start') {
                return (
                  <motion.div key={`render-start-${i}`} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
                    className="my-3 mx-auto max-w-sm p-3 bg-[#3B82F6]/5 border border-[#3B82F6]/30 flex items-center gap-3"
                    data-testid="render-progress-inline">
                    <Loader2 className="w-4 h-4 text-[#3B82F6] animate-spin shrink-0" />
                    <div>
                      <span className="text-[10px] font-mono text-[#3B82F6] block">ART DIRECTOR</span>
                      <span className="text-xs text-[#A0A0A0]">Generating {msg.data.spec_count || '?'} image{(msg.data.spec_count || 0) !== 1 ? 's' : ''}...</span>
                    </div>
                    <ImageIcon className="w-4 h-4 text-[#3B82F6]/50 ml-auto shrink-0" />
                  </motion.div>
                );
              }

              if (msg.type === 'render_complete') {
                return (
                  <motion.div key={`render-done-${i}`} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                    className="my-3 mx-auto max-w-sm p-3 bg-[#22C55E]/5 border border-[#22C55E]/30 flex items-center gap-3"
                    data-testid="render-complete-inline">
                    <ImageIcon className="w-4 h-4 text-[#22C55E] shrink-0" />
                    <div>
                      <span className="text-[10px] font-mono text-[#22C55E] block">RENDER COMPLETE</span>
                      <span className="text-xs text-[#A0A0A0]">{msg.data.asset_count || '?'} asset{(msg.data.asset_count || 0) !== 1 ? 's' : ''} generated (Phase {msg.data.phase})</span>
                    </div>
                  </motion.div>
                );
              }

              return null;
            })}
          </AnimatePresence>
          <div ref={messagesEndRef} />
        </div>

        {/* Asset Panel */}
        <AssetPanel frayId={frayId} renderEvents={renderEvents} />

        {/* Friction Panel */}
        <div className="w-56 border-l border-[#2A2A2A] p-4 shrink-0 hidden lg:block" data-testid="friction-panel">
          <h3 className="text-xs font-mono text-[#A0A0A0] mb-4">FRICTION</h3>

          {/* Friction Meter */}
          <div className="mb-6">
            <div className="relative h-40 w-full bg-[#1A1A1A] border border-[#2A2A2A] overflow-hidden mb-2">
              <motion.div
                className="absolute bottom-0 left-0 right-0"
                animate={{ height: `${tension.score}%` }}
                transition={{ duration: 0.5 }}
                style={{
                  background: `linear-gradient(to top, ${TENSION_COLORS[tension.classification] || '#F59E0B'}20, ${TENSION_COLORS[tension.classification] || '#F59E0B'}05)`,
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl font-bold font-['Barlow_Condensed']" style={{ color: TENSION_COLORS[tension.classification] || '#F59E0B' }}>
                  {Math.round(tension.score)}
                </span>
              </div>
            </div>
            <span className="text-[10px] font-mono capitalize block text-center"
              style={{ color: TENSION_COLORS[tension.classification] }}>
              {tension.classification?.replace('_', ' ')}
            </span>
          </div>

          {/* Voice Status */}
          <h3 className="text-xs font-mono text-[#A0A0A0] mb-3">VOICES</h3>
          <div className="space-y-2">
            {voices.map((voice, i) => {
              const vid = voice.name.toLowerCase().replace(/[\s']/g, '-');
              const state = voiceStates[vid] || 'idle';
              return (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full" style={{
                    backgroundColor: state === 'speaking' ? '#FF4D00' :
                      state === 'thinking' ? '#FF4D00' :
                      state === 'complete' ? '#22C55E' : '#2A2A2A',
                  }} />
                  <span className="text-[10px] font-mono text-[#A0A0A0] truncate">{voice.name}</span>
                </div>
              );
            })}
          </div>

          {/* Phase Info */}
          {currentPhase && (
            <div className="mt-6">
              <h3 className="text-xs font-mono text-[#A0A0A0] mb-2">PHASE</h3>
              <span className="text-sm font-['Barlow_Condensed']">{currentPhase.phase_name}</span>
              <div className="flex gap-1 mt-2">
                {Array.from({ length: currentPhase.total_phases }).map((_, i) => (
                  <div key={i} className={`h-1 flex-1 ${i < currentPhase.phase ? 'bg-[#FF4D00]' : 'bg-[#2A2A2A]'}`} />
                ))}
              </div>
            </div>
          )}

          {/* Polls */}
          <div className="mt-6">
            <PollPanel frayId={frayId} />
          </div>
        </div>
      </div>

      {/* Stopped Fray overlay — offers re-run or dashboard */}
      {showStoppedOverlay && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="stopped-overlay">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-md mx-4 p-6 bg-[#1A1A1A] border border-[#2A2A2A] text-center">
            <Square className="w-8 h-8 text-[#A0A0A0] mx-auto mb-3" />
            <h2 className="text-xl font-bold font-['Barlow_Condensed'] mb-2">Fray Stopped</h2>
            <p className="text-sm text-[#A0A0A0] mb-6">This Fray was stopped before completion. You can re-run it with the same configuration or go back to the dashboard.</p>
            <div className="flex gap-3 justify-center">
              <button onClick={handleRerun}
                className="px-6 py-2.5 bg-[#FF4D00] text-black text-sm font-semibold flex items-center gap-2" data-testid="rerun-stopped">
                <RefreshCw className="w-4 h-4" /> Re-run Fray
              </button>
              <button onClick={() => navigate('/dashboard')}
                className="px-6 py-2.5 border border-[#2A2A2A] text-sm text-[#A0A0A0]" data-testid="dashboard-stopped">
                Dashboard
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Error overlay */}
      {error && !voiceError && !showStoppedOverlay && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="p-6 bg-[#1A1A1A] border border-[#EF4444]/30 max-w-md mx-4 text-center">
            <AlertTriangle className="w-8 h-8 text-[#EF4444] mx-auto mb-3" />
            <p className="text-sm mb-4">{error}</p>
            <button onClick={() => navigate('/dashboard')} className="px-4 py-2 bg-[#FF4D00] text-black text-sm font-medium">
              Back to Dashboard
            </button>
          </div>
        </div>
      )}

      {/* Voice Error Dialog — model picker */}
      {voiceError && (
        <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-50" data-testid="voice-error-dialog">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg mx-4 bg-[#1A1A1A] border border-[#EF4444]/30 overflow-hidden"
          >
            {/* Header */}
            <div className="p-4 border-b border-[#2A2A2A] bg-[#EF4444]/5">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
                <div>
                  <h3 className="text-sm font-semibold font-['Barlow_Condensed'] text-[#F5F5F5]">
                    {voiceError.voice} — Model Error
                  </h3>
                  <p className="text-xs text-[#A0A0A0] mt-1">{voiceError.message}</p>
                </div>
              </div>
            </div>

            {/* Recommendation */}
            <div className="p-4 border-b border-[#2A2A2A]">
              <p className="text-sm text-[#FF4D00] mb-3">
                <RefreshCw className="w-3.5 h-3.5 inline mr-1.5" />
                Select a different model for this voice to continue the Fray:
              </p>
              <div className="text-xs text-[#A0A0A0] mb-3 font-mono">
                Current: <span className="text-[#EF4444]">{voiceError.model}</span>
              </div>

              {/* Model search */}
              <div className="relative mb-2">
                <Search className="w-3.5 h-3.5 text-[#A0A0A0] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text" value={modelSearch} onChange={e => setModelSearch(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] text-xs focus:outline-none focus:border-[#FF4D00]"
                  placeholder="Search for a model..."
                  autoFocus
                  data-testid="voice-error-model-search"
                />
              </div>

              {/* Model list grouped by tier */}
              <TierGroupedModelList
                models={availableModels}
                search={modelSearch}
                onSelect={(modelId) => handleChangeModelAndResume(modelId)}
              />
            </div>

            {/* Actions */}
            <div className="p-4 flex items-center justify-between gap-3">
              <button
                onClick={() => { handleResume(); }}
                className="min-h-[44px] px-4 py-2 text-xs text-[#A0A0A0] border border-[#2A2A2A] hover:text-[#F5F5F5]"
                data-testid="retry-same-model"
              >
                Retry same model
              </button>
              <button
                onClick={handleStop}
                className="min-h-[44px] px-4 py-2 text-xs text-[#EF4444] border border-[#EF4444]/30 hover:bg-[#EF4444]/10"
                data-testid="stop-from-error"
              >
                Stop Fray
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Cat Fight Heckle Bar */}
      {fray?.debate_mode === 'cat_fight' && isStreaming && !isPaused && (
        <CatFightHeckleBar frayId={frayId} voices={voices} />
      )}
    </div>
  );
}

function CatFightHeckleBar({ frayId, voices }) {
  const [text, setText] = useState('');
  const [target, setTarget] = useState('all');
  const [sending, setSending] = useState(false);
  const [lastSent, setLastSent] = useState(0);

  const submitHeckle = async () => {
    if (!text.trim() || sending) return;
    if (Date.now() - lastSent < 10000) return; // Rate limit: 10s
    setSending(true);
    try {
      await API.post(`/api/frays/${frayId}/heckle`, { text: text.trim(), target });
      setText('');
      setLastSent(Date.now());
    } catch (err) {
      console.error('Heckle failed:', err);
    }
    setSending(false);
  };

  return (
    <div className="border-t border-[#EF4444]/30 bg-[#1A1A1A] shrink-0" data-testid="heckle-bar">
      {/* Target buttons — scrollable row */}
      <div className="flex items-center gap-1 px-2 md:px-4 py-1.5 md:py-0 md:pt-1.5 overflow-x-auto">
        <button onClick={() => setTarget('all')}
          className={`min-h-[44px] md:min-h-0 px-3 md:px-2 py-1 text-[9px] font-mono border transition-colors shrink-0 ${target === 'all' ? 'bg-[#EF4444] text-white border-[#EF4444]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#EF4444]/50'}`}>
          Everyone
        </button>
        {voices.map(v => (
          <button key={v.name} onClick={() => setTarget(v.name)}
            className={`min-h-[44px] md:min-h-0 px-3 md:px-2 py-1 text-[9px] font-mono border transition-colors truncate max-w-[80px] shrink-0 ${target === v.name ? 'text-white border-[#EF4444]' : 'border-[#2A2A2A] text-[#A0A0A0] hover:border-[#EF4444]/50'}`}
            style={target === v.name ? { backgroundColor: v.color || '#EF4444', borderColor: v.color || '#EF4444' } : {}}>
            {v.name}
          </button>
        ))}
      </div>
      {/* Input row */}
      <div className="flex items-center gap-2 px-2 md:px-4 pb-2 md:pb-1.5">
        <input value={text} onChange={e => setText(e.target.value.slice(0, 280))}
          onKeyDown={e => e.key === 'Enter' && submitHeckle()}
          className="flex-1 min-w-0 px-3 py-2 md:py-1.5 bg-[#0F0F0F] border border-[#2A2A2A] text-sm focus:outline-none focus:border-[#EF4444] font-mono"
          placeholder={target === 'all' ? 'Shout at everyone...' : `Call out ${target}...`}
          maxLength={280}
          data-testid="heckle-input" />
        <span className="hidden md:inline text-[8px] font-mono text-[#A0A0A0] w-8 text-right">{text.length}</span>
        <button onClick={submitHeckle} disabled={sending || !text.trim() || Date.now() - lastSent < 10000}
          className="min-h-[44px] min-w-[44px] md:min-h-0 md:min-w-0 px-4 py-2 md:py-1.5 bg-[#EF4444] text-white text-xs font-bold font-mono disabled:opacity-30 hover:bg-[#EF4444]/80 transition-colors shrink-0"
          data-testid="heckle-submit">
          {target === 'all' ? 'SHOUT' : 'CALL OUT'}
        </button>
      </div>
    </div>
  );
}


const TIER_ORDER_LIST = [
  { key: 'free', label: 'Free', color: '#A0A0A0' },
  { key: 'economy', label: 'Economy', color: '#22C55E' },
  { key: 'standard', label: 'Standard', color: '#F59E0B' },
  { key: 'premium', label: 'Premium', color: '#EF4444' },
];

function TierGroupedModelList({ models, search, onSelect }) {
  const [expanded, setExpanded] = useState({ free: false, economy: true, standard: true, premium: false });
  const [selecting, setSelecting] = useState(null);

  const handleSelect = (modelId) => {
    setSelecting(modelId);
    onSelect(modelId);
  };

  const filtered = models.filter(m =>
    !search || m.id.toLowerCase().includes(search.toLowerCase()) || m.name?.toLowerCase().includes(search.toLowerCase())
  );

  // Group by tier
  const grouped = {};
  for (const m of filtered) {
    const t = m.tier || 'economy';
    if (!grouped[t]) grouped[t] = [];
    grouped[t].push(m);
  }

  // If searching, expand all tiers that have results
  const effectiveExpanded = search
    ? Object.fromEntries(TIER_ORDER_LIST.map(t => [t.key, (grouped[t.key]?.length || 0) > 0]))
    : expanded;

  return (
    <div className="max-h-64 overflow-y-auto border border-[#2A2A2A]">
      {TIER_ORDER_LIST.map(tier => {
        const tierModels = grouped[tier.key] || [];
        if (tierModels.length === 0) return null;
        const isOpen = effectiveExpanded[tier.key];

        return (
          <div key={tier.key}>
            <button
              onClick={() => setExpanded(prev => ({ ...prev, [tier.key]: !prev[tier.key] }))}
              className="w-full flex items-center justify-between px-3 py-2 bg-[#1A1A1A] border-b border-[#2A2A2A] hover:bg-[#2A2A2A]/50 sticky top-0 z-10"
            >
              <div className="flex items-center gap-2">
                <span className="text-[9px] px-2 py-0.5 border font-mono font-bold"
                  style={{ color: tier.color, borderColor: tier.color + '40' }}>
                  {tier.label.toUpperCase()}
                </span>
                <span className="text-[10px] text-[#A0A0A0] font-mono">{tierModels.length} models</span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-[#A0A0A0] transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>
            {isOpen && tierModels.slice(0, 40).map(m => (
              <button
                key={m.id}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleSelect(m.id); }}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-left border-b border-[#0F0F0F] cursor-pointer ${
                  selecting === m.id ? 'bg-[#FF4D00]/20 text-[#FF4D00]' : 'hover:bg-[#FF4D00]/10'
                }`}
                data-testid={`tier-model-${m.id}`}
              >
                <span className="text-xs text-[#F5F5F5] truncate">{m.name || m.id}</span>
                <span className="text-[8px] font-mono text-[#A0A0A0] shrink-0 ml-2">
                  {selecting === m.id ? 'Selecting...' : m.id.split('/')[0]}
                </span>
              </button>
            ))}
          </div>
        );
      })}
      {filtered.length === 0 && (
        <div className="p-4 text-center text-xs text-[#A0A0A0]">No models match your search</div>
      )}
    </div>
  );
}



function StreamingReasoningBlock({ voice, data }) {
  const [collapsed, setCollapsed] = useState(false);
  const color = voice?.color || '#A0A0A0';
  const isComplete = data.complete;
  const endRef = useRef(null);

  useEffect(() => {
    if (!collapsed) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data.text, collapsed]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-3">
      <button onClick={() => setCollapsed(!collapsed)} className="flex items-center gap-2 px-3 py-1.5 group">
        <ChevronRight className={`w-3.5 h-3.5 text-[#A855F7] transition-transform ${!collapsed ? 'rotate-90' : ''}`} />
        <span className="text-xs text-[#A855F7] italic">
          {isComplete ? 'Thought for a while' : 'Thinking...'}
        </span>
        {!isComplete && <div className="w-2 h-2 bg-[#A855F7] rounded-full animate-pulse" />}
        {data.reasoning_tokens > 0 && <span className="text-[9px] text-[#A0A0A0] font-mono">~{data.reasoning_tokens?.toLocaleString()} tokens</span>}
      </button>
      {!collapsed && (
        <div className="ml-6 pl-3 border-l-2 border-[#A855F7]/30 max-h-80 overflow-y-auto">
          <div className="text-xs text-[#B0B0B0] whitespace-pre-wrap leading-relaxed py-1">
            {data.text || ''}
            {!isComplete && <span className="inline-block w-1.5 h-3.5 bg-[#A855F7] ml-0.5 animate-pulse" />}
          </div>
          <div ref={endRef} />
        </div>
      )}
    </motion.div>
  );
}


function ReasoningBlock({ voice, data }) {
  const [expanded, setExpanded] = useState(false);
  const color = voice?.color || '#A0A0A0';

  return (
    <motion.div
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-2"
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 px-3 py-2 text-left hover:bg-[#2A2A2A]/20 transition-colors group"
        data-testid={`reasoning-toggle-${data.voice_id}`}
      >
        <ChevronRight className={`w-3.5 h-3.5 text-[#A855F7] transition-transform ${expanded ? 'rotate-90' : ''}`} />
        <span className="text-xs text-[#A855F7] italic">Thought for a while</span>
        <span className="text-[9px] text-[#A0A0A0] font-mono">~{data.reasoning_tokens?.toLocaleString()} tokens</span>
      </button>
      {expanded && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          className="ml-6 pl-3 border-l-2 border-[#A855F7]/30 mb-3"
        >
          <div className="text-xs text-[#C0C0C0] whitespace-pre-wrap leading-relaxed max-h-96 overflow-y-auto py-2">
            {data.reasoning}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
}
